import { useTranslation } from "react-i18next"
import {
  Tag,
  HandCoins,
  Steps,
  MapPin,
  Factory,
  ClockCounterClockwise,
  MagnifyingGlass,
  Graph,
  LockSimple,
  CaretRight,
  Pencil,
} from "@phosphor-icons/react"
import { useMemo } from "react"
import { Link } from "react-router"
import { Button } from "@repo/ui"
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx"
import { ScrollableRouteContainer } from "@/components/shared/Container.tsx"
import { PageBody, PageHeader } from "@/components/shared/page-sections.tsx"

export const FundraisingAdvisoryRoute = () => {
  const { t } = useTranslation(`fundraising`)
  const { t: tCommon } = useTranslation(`common`)

  const sectionCardLinks = useMemo(
    () => [
      {
        key: `funding_history`,
        link: `funding-history`,
        title: t(`fundraising.index.section_links.funding_history.title`),
        description: t(
          `fundraising.index.section_links.funding_history.description`
        ),
        icon: ClockCounterClockwise,
        isDisabled: false,
      },
      {
        key: `vc_matching`,
        link: `vc-matching`,
        title: t(`fundraising.index.section_links.vc_matching.title`),
        description: t(
          `fundraising.index.section_links.vc_matching.description`
        ),
        icon: MagnifyingGlass,
        isDisabled: false,
      },
      {
        key: `comps_funding_analysis`,
        link: `competitors-funding-analysis`,
        title: t(
          `fundraising.index.section_links.comps_funding_analysis.title`
        ),
        description: t(
          `fundraising.index.section_links.comps_funding_analysis.description`
        ),
        icon: Graph,
        isDisabled: false,
      },
    ],
    [t]
  )
  return (
    <ScrollableRouteContainer className={`p-0`}>
      <PageHeader className={`flex flex-col gap-1`}>
        <h1 className={`page-title`}>{t(`fundraising.index.title`)}</h1>
        <h3 className={`page-subtitle`}>{t(`fundraising.index.subtitle`)}</h3>
      </PageHeader>
      <PageBody className={`flex flex-col gap-6`}>
        <Card className={`grid grid-cols-6 gap-6 p-8`}>
          <h4 className={`font-medium text-xl text-primary col-span-4`}>
            {t(`fundraising.index.business_informations.title`)}
          </h4>
          <div className={`flex col-span-2 justify-end`}>
            <Link to={`business-information`}>
              <Button size={`md`} className={`flex gap-2 items-center`}>
                <Pencil className={`shrink-0`} size={20} />
                <span>{tCommon(`buttons.edit`)}</span>
              </Button>
            </Link>
          </div>
          <div className={`col-span-2 flex gap-4`}>
            <HandCoins className={`shrink-0 self-center`} size={16} />
            <div>
              <h5 className={`font-normal text-sm text-secondary`}>
                {t(`fundraising.index.business_informations.funding_needs`)}
              </h5>
              <p className={`font-normal text-lg text-primary`}>$1.2M</p>
            </div>
          </div>
          <div className={`col-span-2 flex gap-4`}>
            <Steps className={`shrink-0 self-center`} size={16} />
            <div>
              <h5 className={`font-normal text-sm text-secondary`}>
                {t(`fundraising.index.business_informations.lifecycle_stage`)}
              </h5>
              <p className={`font-normal text-lg text-primary`}>
                Early - Stage
              </p>
            </div>
          </div>
          <div className={`col-span-2 flex gap-4`}>
            <MapPin className={`shrink-0 self-center`} size={16} />
            <div>
              <h5 className={`font-normal text-sm text-secondary`}>
                {t(`fundraising.index.business_informations.country`)}
              </h5>
              <p className={`font-normal text-lg text-primary`}>Italy</p>
            </div>
          </div>
          <div className={`col-span-3 flex gap-4`}>
            <Tag className={`self-center shrink-0`} size={16} />
            <div className={`flex flex-col gap-1`}>
              <h6 className={`font-normal text-sm text-secondary`}>
                {t(`fundraising.index.business_informations.tags`)}
              </h6>
              <div className={`flex flex-wrap gap-1`}>
                {[
                  `Machine Learning`,
                  `Artificial Intelligence`,
                  ...Array.from({ length: 11 }, (_, i) => `tag ${i + 3}`),
                ].map((tag) => (
                  <span
                    key={tag}
                    className={`text-sm text-gray-600 bg-gray-50 rounded px-3 py-1 whitespace-nowrap`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className={`col-span-3 flex gap-4`}>
            <Factory className={`self-center shrink-0`} size={16} />
            <div className={`flex flex-col  gap-1`}>
              <h6 className={`font-normal text-sm text-secondary`}>
                {t(`fundraising.index.business_informations.industries`)}
              </h6>
              <div className={`flex flex-wrap gap-1`}>
                {[
                  `Advertising Agencies`,
                  `Market Research & Consulting E-Marketing`,
                  `Marketing Consulting Services`,
                  `Public Relation Agencies`,
                ].map((tag) => (
                  <span
                    key={tag}
                    className={`text-sm text-gray-600 bg-gray-50 rounded px-3 py-1 whitespace-nowrap`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Card>
        <div className={`grid grid-cols-3 gap-6`}>
          {sectionCardLinks.map(
            ({ key, title, description, icon: Icon, link, isDisabled }) => (
              <Link
                key={key}
                to={link}
                aria-disabled={isDisabled}
                className={`aria-disabled:cursor-not-allowed aria-disabled:pointer-events-none shadow-loose-plus bg-transparent rounded-xl flex`}
              >
                <Card
                  className={`border border-gray-200 hover:bg-gray-50 group rounded-xl`}
                  disabled={isDisabled}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between gap-4">
                      <div className={`flex flex-col gap-2`}>
                        <div className={`flex gap-3 items-center`}>
                          <div
                            className={`rounded-lg w-8 h-8 bg-gray-50 group:hover:bg-gray-500 flex justify-center items-center`}
                          >
                            <Icon size={20} />
                          </div>
                          <CardTitle className="text-lg text-primary font-medium">
                            {title}
                          </CardTitle>
                        </div>
                        <CardDescription className="text-base text-secondary font-normal">
                          {description}
                        </CardDescription>
                      </div>
                      <div
                        className={`px-0.5 py-1 shrink-0 flex justify-center items-center`}
                      >
                        {isDisabled ? (
                          <LockSimple size={24} className={`text-disabled`} />
                        ) : (
                          <CaretRight size={24} className="text-gray-500" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </Link>
            )
          )}
        </div>
      </PageBody>
    </ScrollableRouteContainer>
  )
}
